import {
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ProductMark } from "./Avatar";
import { EmojiPicker } from "./EmojiPicker";
import { CameraIcon, ClockIcon, MicIcon, PlusIcon, ReplyIcon, SendIcon, SmileIcon, XIcon } from "./icons";
import { cn, focusRing } from "./utils";

export type ComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onAttach?: () => void;
  onVoice?: () => void;
  onCamera?: () => void;
  /** Replaces the built-in emoji picker, which otherwise opens from the Emoji button. */
  onEmoji?: () => void;
  /** Opens Queravel. Leave it out where Queravel may not be asked, and the button is hidden. */
  onAskQueravel?: () => void;
  /** Picks a time to send what is typed. Shown once there is text, beside Send. */
  onSchedule?: () => void;
  placeholder?: string;
  replyTo?: { author: string; text: string };
  onCancelReply?: () => void;
  variant?: "mobile" | "desktop";
  /** Group chats lead with the Queravel button in place of attach. */
  assistLeading?: boolean;
  hint?: string;
  disabled?: boolean;
  /** Hold to record. The app follows the pointer from here to slide to cancel and release to send. */
  onVoicePress?: (e: PointerEvent<HTMLButtonElement>) => void;
  /** Shown in place of the text field while a voice message records. */
  recording?: ComposerRecording;
};

export type ComposerRecording = {
  elapsed: string;
  cancelling?: boolean;
  /** Buttons for people recording with a keyboard, who cannot hold and slide. */
  onStop?: () => void;
  onCancel?: () => void;
};

/** The line that replaces the text field while a voice message records. */
export function RecordingBar({ recording, className }: { recording: ComposerRecording; className?: string }) {
  const { elapsed, cancelling, onStop, onCancel } = recording;
  return (
    <div className={cn("flex min-h-[46px] min-w-0 flex-1 items-center gap-2.5 px-3", className)}>
      <span aria-hidden="true" className="h-2.5 w-2.5 flex-none animate-pulse rounded-full bg-scarlet" />
      <span className="flex-none text-[15px] font-semibold tabular-nums">{elapsed}</span>
      <span role="status" className="min-w-0 flex-1 truncate text-sm text-ink-secondary">
        {cancelling ? "Release to cancel" : onStop ? "Recording a voice message" : "Slide left to cancel"}
      </span>
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className={cn(
            "cursor-pointer rounded-full px-2.5 py-1 text-sm font-semibold text-scarlet",
            focusRing,
          )}
        >
          Cancel
        </button>
      )}
      {onStop && (
        <button
          type="button"
          onClick={onStop}
          className={cn(
            "cursor-pointer rounded-full bg-blue px-3 py-1 text-sm font-semibold text-white",
            focusRing,
          )}
        >
          Send
        </button>
      )}
    </div>
  );
}

/** The tallest the text field grows, matching max-h-32, before it scrolls. */
const MAX_FIELD_HEIGHT = 128;

function sizesItself(): boolean {
  return (
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("field-sizing", "content")
  );
}

function QueravelButton({ onClick, prominent }: { onClick?: () => void; prominent: boolean }) {
  return (
    <button
      type="button"
      aria-label={prominent ? "Ask Queravel about this chat" : "Ask Queravel"}
      onClick={onClick}
      className={cn(
        "flex flex-none cursor-pointer items-center justify-center rounded-[12px]",
        prominent ? "h-[46px] w-[46px]" : "h-[38px] w-[38px]",
        focusRing,
      )}
    >
      <ProductMark product="queravel" size={prominent ? 42 : 28} />
    </button>
  );
}

export function Composer({
  value,
  onChange,
  onSend,
  onAttach,
  onVoice,
  onCamera,
  onEmoji,
  onAskQueravel,
  onSchedule,
  placeholder = "Message",
  replyTo,
  onCancelReply,
  variant = "mobile",
  assistLeading = false,
  hint,
  disabled = false,
  onVoicePress,
  recording,
}: ComposerProps) {
  const hintId = useId();
  const hasText = value.trim().length > 0;
  const field = useRef<HTMLTextAreaElement>(null);
  const emojiButton = useRef<HTMLButtonElement>(null);
  const emojiPanel = useRef<HTMLDivElement>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  // Where the caret goes once the text with a new emoji is back from the parent.
  const caret = useRef<number | null>(null);

  // The field grows with its text. Browsers without field-sizing, such as older Safari and Firefox,
  // are measured here instead, up to the same height, after which the field scrolls.
  // biome-ignore lint/correctness/useExhaustiveDependencies: measures again when the text changes
  useLayoutEffect(() => {
    const el = field.current;
    if (!el || sizesItself()) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_FIELD_HEIGHT)}px`;
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once the new text is in the field
  useLayoutEffect(() => {
    const at = caret.current;
    if (at === null || !field.current) return;
    caret.current = null;
    field.current.focus();
    field.current.setSelectionRange(at, at);
  }, [value]);

  // A click anywhere else closes the emoji picker.
  useEffect(() => {
    if (!emojiOpen) return;
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (emojiPanel.current?.contains(t) || emojiButton.current?.contains(t)) return;
      setEmojiOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [emojiOpen]);

  /** Puts the emoji where the caret is, or over the selected text, and leaves the caret after it. */
  const insertEmoji = (emoji: string, keepOpen: boolean) => {
    const el = field.current;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    caret.current = start + emoji.length;
    onChange(value.slice(0, start) + emoji + value.slice(end));
    if (!keepOpen) setEmojiOpen(false);
  };

  const closeEmoji = () => {
    setEmojiOpen(false);
    field.current?.focus();
  };
  // Without a handler there is no Queravel button, so attach takes the lead again.
  const leadWithQueravel = assistLeading && Boolean(onAskQueravel);

  // A pointer press records through onVoicePress. The click that follows it is ignored, so only a
  // keyboard press (detail 0) reaches onVoice.
  const voiceClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!onVoicePress || e.detail === 0) onVoice?.();
  };

  function submit(e?: FormEvent) {
    e?.preventDefault();
    if (hasText && !disabled) onSend();
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  }

  const textarea = (
    <textarea
      ref={field}
      aria-label="Message"
      aria-describedby={hint ? hintId : undefined}
      rows={1}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      className={cn(
        "field-sizing-content max-h-32 min-w-0 flex-1 resize-none border-0 bg-transparent py-0 font-sans text-ink outline-none placeholder:text-ink-meta",
        variant === "mobile" ? "text-base" : "text-[15px]",
      )}
    />
  );

  const replyBar = replyTo && (
    <div className="mx-3 mt-2.5 flex items-center gap-2.5 rounded-field bg-surface-header py-2 pr-2 pl-3.5 min-[900px]:mx-0 min-[900px]:mt-0 min-[900px]:mb-2">
      <ReplyIcon size={17} strokeWidth={2.2} className="flex-none text-blue" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[12.5px] font-bold text-blue">Replying to {replyTo.author}</span>
        <span className="truncate text-[13.5px] text-ink-secondary">{replyTo.text}</span>
      </span>
      <button
        type="button"
        aria-label="Cancel reply"
        onClick={onCancelReply}
        className={cn(
          "flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-ink-secondary",
          focusRing,
        )}
      >
        <XIcon size={15} strokeWidth={2.4} />
      </button>
    </div>
  );

  if (variant === "desktop") {
    return (
      <form
        onSubmit={submit}
        className="relative flex-none border-t border-line bg-linen px-5 pt-3 pb-[18px]"
      >
        {emojiOpen && (
          <div
            ref={emojiPanel}
            role="dialog"
            aria-label="Emoji"
            className="absolute right-5 bottom-full z-30 mb-2"
          >
            <EmojiPicker label="Add an emoji" onPick={insertEmoji} onClose={closeEmoji} />
          </div>
        )}
        {replyBar}
        <div className="flex items-center gap-2 rounded-card border border-line bg-surface py-1.5 pr-1.5 pl-2.5 focus-within:border-blue">
          <button
            type="button"
            aria-label="Attach"
            onClick={onAttach}
            className={cn(
              "flex h-[38px] w-[38px] flex-none cursor-pointer items-center justify-center rounded-[10px] bg-surface-header text-ink",
              focusRing,
            )}
          >
            <PlusIcon size={19} strokeWidth={2.4} />
          </button>
          {recording ? <RecordingBar recording={recording} className="px-1" /> : textarea}
          <button
            ref={emojiButton}
            type="button"
            aria-label="Emoji"
            aria-haspopup="dialog"
            aria-expanded={onEmoji ? undefined : emojiOpen}
            onClick={onEmoji ?? (() => setEmojiOpen((open) => !open))}
            className={cn(
              "flex h-[38px] w-[38px] flex-none cursor-pointer items-center justify-center rounded-[10px] text-ink-secondary hover:bg-surface-header",
              focusRing,
            )}
          >
            <SmileIcon size={19} />
          </button>
          <button
            type="button"
            aria-label={onVoicePress ? "Hold to record a voice message" : "Record voice message"}
            aria-pressed={recording ? true : undefined}
            onClick={voiceClick}
            onPointerDown={onVoicePress}
            className={cn(
              "flex h-[38px] w-[38px] flex-none cursor-pointer touch-none items-center justify-center rounded-[10px] hover:bg-surface-header",
              recording ? "bg-surface-header text-scarlet" : "text-ink-secondary",
              focusRing,
            )}
          >
            <MicIcon size={19} strokeWidth={2.2} />
          </button>
          {onSchedule && hasText && (
            <button
              type="button"
              aria-label="Schedule message"
              onClick={onSchedule}
              disabled={disabled}
              className={cn(
                "flex h-[38px] w-[38px] flex-none cursor-pointer items-center justify-center rounded-[10px] text-ink-secondary hover:bg-surface-header",
                focusRing,
              )}
            >
              <ClockIcon size={19} strokeWidth={2.2} />
            </button>
          )}
          <button
            type="submit"
            aria-label="Send"
            disabled={!hasText || disabled}
            className={cn(
              "flex h-[42px] w-[42px] flex-none cursor-pointer items-center justify-center rounded-field bg-blue text-white disabled:cursor-default disabled:opacity-60",
              focusRing,
            )}
          >
            <SendIcon size={18} strokeWidth={2.2} />
          </button>
        </div>
        {hint && (
          <p id={hintId} className="m-0 mt-2 text-xs text-ink-meta">
            {hint}
          </p>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="flex-none border-t border-line bg-linen">
      {replyBar}
      <div
        className={cn(
          "flex items-end gap-2 px-3 pb-[max(24px,env(safe-area-inset-bottom))]",
          replyTo ? "pt-2" : "pt-2.5",
        )}
      >
        {leadWithQueravel ? (
          <QueravelButton onClick={onAskQueravel} prominent />
        ) : (
          <button
            type="button"
            aria-label="Attach"
            onClick={onAttach}
            className={cn(
              "flex h-[46px] w-[46px] flex-none cursor-pointer items-center justify-center rounded-full bg-surface-header text-ink",
              focusRing,
            )}
          >
            <PlusIcon size={21} strokeWidth={2.2} />
          </button>
        )}
        <div
          className={cn(
            "flex min-h-[46px] min-w-0 flex-1 items-center gap-0.5 rounded-[23px] border border-line bg-surface py-1 pr-1 pl-4",
            "focus-within:border-blue focus-within:shadow-[inset_0_0_0_0.5px_var(--color-blue)]",
          )}
        >
          {recording ? <RecordingBar recording={recording} className="-ml-3 px-0" /> : textarea}
          {!leadWithQueravel && !recording && onAskQueravel && (
            <QueravelButton onClick={onAskQueravel} prominent={false} />
          )}
          {leadWithQueravel ? (
            <button
              type="button"
              aria-label="Attach"
              onClick={onAttach}
              className={cn(
                "flex h-[38px] w-[38px] flex-none cursor-pointer items-center justify-center rounded-full text-ink-secondary",
                focusRing,
              )}
            >
              <PlusIcon size={20} />
            </button>
          ) : (
            <button
              type="button"
              aria-label="Camera"
              onClick={onCamera}
              className={cn(
                "flex h-[38px] w-[38px] flex-none cursor-pointer items-center justify-center rounded-full text-ink-secondary",
                focusRing,
              )}
            >
              <CameraIcon size={20} />
            </button>
          )}
        </div>
        {hasText && onSchedule && (
          <button
            type="button"
            aria-label="Schedule message"
            onClick={onSchedule}
            disabled={disabled}
            className={cn(
              "flex h-12 w-10 flex-none cursor-pointer items-center justify-center rounded-full text-ink-secondary",
              focusRing,
            )}
          >
            <ClockIcon size={21} strokeWidth={2.2} />
          </button>
        )}
        {hasText ? (
          <button
            type="submit"
            aria-label="Send"
            disabled={disabled}
            className={cn(
              "flex h-12 w-12 flex-none cursor-pointer items-center justify-center rounded-full bg-blue text-white",
              focusRing,
            )}
          >
            <SendIcon size={20} strokeWidth={2.2} />
          </button>
        ) : (
          <button
            type="button"
            aria-label="Hold to record a voice message"
            aria-pressed={recording ? true : undefined}
            onClick={voiceClick}
            onPointerDown={onVoicePress}
            className={cn(
              "flex h-12 w-12 flex-none cursor-pointer touch-none items-center justify-center rounded-full bg-blue text-white",
              recording && "scale-110",
              focusRing,
            )}
          >
            <MicIcon size={21} strokeWidth={2.2} />
          </button>
        )}
      </div>
    </form>
  );
}
